import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { space as styledSpace, SpaceProps } from 'styled-system';

const avatarSizes: Record<string, number> = {
  xxs: 28,
  xs: 34,
  s: 46,
  m: 52,
  l: 64,
  xl: 82,
};

const textSizes: Record<string, number> = {
  xxs: 11,
  xs: 12,
  s: 14,
  m: 14,
  l: 14,
  xl: 22,
};

function getPlaceholderText(letter?: string | null): string {
  if (!letter) return '';
  const parts = letter.replace(/['"().«»,-]+/g, '').split(' ');
  if (parts.length < 2) return parts[0].slice(0, 2).toUpperCase();
  return parts
    .slice(0, 2)
    .reduce((acc, word) => acc + (word[0]?.toUpperCase() ?? ''), '');
}

const EmptyAvatar = styled.div<{ $size: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.highlight.blue25};
  color: ${({ theme }) => theme.colors.primary.main};
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  font-size: ${({ $size }) => textSizes[$size] ?? 14}px;
`;

const WrapperAvatar = styled.div<{
  $size: string;
  $rounded: boolean;
  $hasImage: boolean;
}>`
  position: relative;
  width: ${({ $size }) => avatarSizes[$size] ?? avatarSizes.m}px;
  min-width: ${({ $size }) => avatarSizes[$size] ?? avatarSizes.m}px;
  height: ${({ $size }) => avatarSizes[$size] ?? avatarSizes.m}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme, $rounded }) =>
    $rounded ? '50%' : `${theme.borderRadius}px`};
  background: ${({ theme }) => theme.colors.white};
  overflow: hidden;

  ${({ $hasImage, theme, $rounded }) =>
    $hasImage &&
    css`
      img {
        object-fit: cover;
        height: 100%;
        width: 100%;
      }
      ${$rounded &&
      css`
        border: 1px solid ${theme.colors.white};
      `}
    `}

  ${styledSpace}
`;

export type AvatarSize = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';

export interface AvatarProps extends SpaceProps {
  url?: string;
  letter?: string | null;
  rounded?: boolean;
  size?: AvatarSize;
  alt?: string;
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      url = '',
      letter,
      rounded = true,
      size = 'm',
      alt = 'avatar',
      ...spaceProps
    },
    ref,
  ) => {
    const placeholderText = getPlaceholderText(letter);
    const hasImage = Boolean(url);

    return (
      <WrapperAvatar
        ref={ref}
        $size={size}
        $rounded={rounded}
        $hasImage={hasImage}
        {...spaceProps}
      >
        {url ? (
          <img
            alt={alt}
            src={url}
            width={avatarSizes[size]}
            height={avatarSizes[size]}
          />
        ) : (
          <EmptyAvatar $size={size} aria-label={placeholderText || 'Avatar'}>
            {letter ? (
              placeholderText
            ) : (
              <svg
                width={avatarSizes[size] * 0.5}
                height={avatarSizes[size] * 0.5}
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </EmptyAvatar>
        )}
      </WrapperAvatar>
    );
  },
);

Avatar.displayName = 'Avatar';
